/**
 * A client template's own title page in the live document.
 *
 * A private workspace template whose content before the first heading is a page
 * of its own keeps that page as the document's title page (its wording, layout
 * and logo; only its fill-in fields change). The doc-type config flags it as
 * ``title_page.cover``. The server renders the page, filled with the run's
 * title fields, and it goes into Word whole — the same page the downloaded
 * .docx opens with — instead of the standard centred title + field table.
 */
import { type DocTypeConfig, renderRunSkeleton, renderTemplateCover } from "../../api/client";
import { applyTemplateCover, scaffoldFromServerDocument } from "../../services/wordWriter";

/** Does this doc-type config use the template's own title page? */
export function hasTemplateCover(titlePage: unknown): boolean {
  return Boolean(titlePage && typeof titlePage === "object" && (titlePage as { cover?: unknown }).cover);
}

/** Scaffold options for a template title page: no standard title chrome (the
 *  template page replaces it). Empty for every other doctype. */
export function templateCoverScaffoldOverrides(titlePage: unknown): {
  documentTitle?: string;
  titlePageFields?: undefined;
  confidentiality?: null;
} {
  return hasTemplateCover(titlePage) ? { documentTitle: "", titlePageFields: undefined, confidentiality: null } : {};
}

/** Render the document's title page with ``fields`` — server-side, the page the
 *  downloaded .docx opens with (the template's own page, or the submission
 *  type's) — and put it at the top of the live document. False when there is
 *  none or Word refused it. */
export async function writeTemplateCover(docType: string, fields: Record<string, string>): Promise<boolean> {
  const { docx_base64 } = await renderTemplateCover(docType, fields);
  if (!docx_base64) return false;
  return applyTemplateCover(docx_base64);
}

/** Write the run's title page into the live document the way "Load doc" shows
 *  it: the server-rendered page for every doctype, so a live-drafted cover and a
 *  Loaded one are the same page. ``fallback`` (the add-in's own renderer) runs
 *  only when the server has no page or Word refused it. */
export async function writeTitlePage(
  docType: string | undefined,
  fields: Record<string, string>,
  fallback: () => Promise<unknown>,
): Promise<void> {
  const written = docType
    ? await writeTemplateCover(docType, fields).catch((err) => {
        console.warn("[templateCover] server title page failed — using the add-in renderer:", err);
        return false;
      })
    : false;
  if (!written) await fallback();
}

/** Scaffold the live document of a client-template run from the server's
 *  render of the empty document — the file "Load doc" inserts, so live drafting
 *  and a Load share headings, title page, page setup and styles. Headings
 *  written (0 = the document already had content), or null when the server
 *  has no render or Word refused it: the caller builds its own scaffold. */
export async function scaffoldFromTemplateRender(runId: string, config: DocTypeConfig): Promise<number | null> {
  try {
    const { docx_base64 } = await renderRunSkeleton(runId);
    if (!docx_base64) return null;
    return await scaffoldFromServerDocument(docx_base64, {
      styleSpec: config.live_doc_style_spec,
      skeleton: config.skeleton,
      headingNumbering: config.heading_numbering,
    });
  } catch (err) {
    console.warn("[templateCover] server scaffold failed — using the add-in scaffold:", err);
    return null;
  }
}
